/**
 * Shop rail PRESSURE RANGE slider (template-parts/shop-filter-rail.php,
 * shown when Theme Options → Shop filters includes Pressure range).
 *
 * Two range inputs move over the psi stops in data-stops (indices, not
 * psi, so low ranges are as easy to pick as 30K). They write the hidden
 * prmin / prmax inputs the form submits (text inputs with `hidden`, so a
 * form reset restores them); those are disabled — left out of the URL —
 * while the range spans the whole slider.
 *   - Dragging one thumb past the other pushes it along (as the price
 *     slider does).
 *   - A press on the track moves the nearer thumb there.
 *   - Thumbs closer than their width are drawn apart (shop.scss
 *     .is-touching, as on the price slider), so both stay grabbable —
 *     a one-stop range included.
 *   - A release, track press or quick-range pick commits through
 *     `shop:filter-change` on the form — shop-ajax.js on /shop/, the
 *     Product Grid block's driver on its rail.
 *   - `shop:state-restored` (back / forward, CLEAR ALL) and the block's
 *     native form reset repaint from the hidden inputs.
 */
const fmt = (v) => Number(v).toLocaleString('en-US');

const initWrap = (wrap) => {
    const group = wrap.closest('details') || wrap.parentElement;
    const form  = wrap.closest('form');
    const lo    = wrap.querySelector('[data-shop-pressure-lo]');
    const hi    = wrap.querySelector('[data-shop-pressure-hi]');
    const min   = wrap.querySelector('[data-shop-pressure-min]');
    const max   = wrap.querySelector('[data-shop-pressure-max]');
    const fill  = wrap.querySelector('.tx-shop__rng-fill');
    let stops = [];
    try {
        stops = JSON.parse(wrap.dataset.stops || '[]');
    } catch {
        stops = [];
    }
    const last = stops.length - 1;
    if (!lo || !hi || !min || !max || !fill || last < 1) return;

    const minLabel = group.querySelector('[data-shop-pressure-min-label]');
    const maxLabel = group.querySelector('[data-shop-pressure-max-label]');
    const presets  = [...group.querySelectorAll('[data-shop-pressure-preset]')];

    // Clamped first: past ~1e21 every distance rounds equal.
    const nearest = (psi) => {
        const at = Math.min(Math.max(Number(psi) || 0, 0), stops[last]);
        return stops.reduce((best, stop, i) => (Math.abs(stop - at) < Math.abs(stops[best] - at) ? i : best), 0);
    };
    const pct = (i) => (i / last) * 100;

    // Thumb width in px — the .tx-shop__rng-wrap thumbs in shop.scss.
    const THUMB_PX = 16;

    const separate = () => {
        const width = wrap.getBoundingClientRect().width;
        if (width <= 0) return;
        const overlap = THUMB_PX - ((Number(hi.value) - Number(lo.value)) / last) * width;
        wrap.classList.toggle('is-touching', overlap > 0);
        if (overlap > 0) {
            wrap.style.setProperty('--rng-touch-shift', `${overlap / 2}px`);
        } else {
            wrap.style.removeProperty('--rng-touch-shift');
        }
    };

    const paint = () => {
        const a  = Number(lo.value);
        const b  = Number(hi.value);
        const on = a > 0 || b < last;
        fill.style.left  = `${pct(a)}%`;
        fill.style.right = `${100 - pct(b)}%`;
        if (minLabel) minLabel.textContent = fmt(stops[a]);
        if (maxLabel) maxLabel.textContent = fmt(stops[b]);
        lo.setAttribute('aria-valuetext', `${fmt(stops[a])} psi`);
        hi.setAttribute('aria-valuetext', `${fmt(stops[b])} psi`);
        presets.forEach((button) => {
            const pressed = on && stops[a] === Number(button.dataset.min) && stops[b] === Number(button.dataset.max);
            button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
            button.classList.toggle('is-on', pressed);
            if (pressed) button.disabled = false;
        });
        separate();
    };

    // The hidden inputs follow the slider; a full-width range is none.
    const sync = () => {
        const a  = Number(lo.value);
        const b  = Number(hi.value);
        const on = a > 0 || b < last;
        min.value    = on ? String(stops[a]) : '';
        max.value    = on ? String(stops[b]) : '';
        min.disabled = !on;
        max.disabled = !on;
    };

    const commit = () => {
        if (form) form.dispatchEvent(new CustomEvent('shop:filter-change', { bubbles: true }));
    };

    lo.addEventListener('input', () => {
        if (Number(lo.value) > Number(hi.value)) hi.value = lo.value;
        sync();
        paint();
    });
    hi.addEventListener('input', () => {
        if (Number(hi.value) < Number(lo.value)) lo.value = hi.value;
        sync();
        paint();
    });
    lo.addEventListener('change', commit);
    hi.addEventListener('change', commit);

    // Only the thumbs take pointer events (shop.scss), so a press on
    // the bare track lands on the wrap: move the nearer thumb there. A
    // thumb drag released over the track also clicks the wrap — skip it.
    let fromThumb = false;
    wrap.addEventListener('pointerdown', (e) => {
        fromThumb = e.target === lo || e.target === hi;
    });
    wrap.addEventListener('click', (e) => {
        if (fromThumb || e.target === lo || e.target === hi) return;
        const box = wrap.getBoundingClientRect();
        if (box.width <= THUMB_PX) return;
        // A thumb's centre runs from THUMB_PX / 2 to width − THUMB_PX / 2.
        const at = (e.clientX - box.left - THUMB_PX / 2) / (box.width - THUMB_PX);
        const i  = Math.max(0, Math.min(last, Math.round(at * last)));
        const a = Number(lo.value);
        const b = Number(hi.value);
        const thumb = i <= a || (i < b && i - a <= b - i) ? lo : hi;
        thumb.value = String(i);
        sync();
        paint();
        commit();
    });

    presets.forEach((button) => {
        // A count refresh leaves the focused quick range enabled (disabling
        // it would drop focus): settle it once focus moves on.
        button.addEventListener('blur', () => {
            const n = button.querySelector('[data-shop-count]');
            if (n) button.disabled = n.textContent.trim() === '0' && button.getAttribute('aria-pressed') !== 'true';
        });
        button.addEventListener('click', () => {
            const a = stops.indexOf(Number(button.dataset.min));
            const b = stops.indexOf(Number(button.dataset.max));
            if (a < 0 || b < 0) return;
            const pressed = button.getAttribute('aria-pressed') === 'true';
            lo.value = String(pressed ? 0 : a);
            hi.value = String(pressed ? last : b);
            sync();
            paint();
            commit();
        });
    });

    const restore = () => {
        lo.value = String(min.disabled || min.value === '' ? 0 : nearest(Number(min.value)));
        hi.value = String(max.disabled || max.value === '' ? last : nearest(Number(max.value)));
        if (Number(lo.value) > Number(hi.value)) lo.value = hi.value;
        sync();
        paint();
    };
    document.addEventListener('shop:state-restored', restore);
    // A closed group has no width to measure; measure again on open.
    window.addEventListener('resize', separate);
    if (group.tagName === 'DETAILS') {
        group.addEventListener('toggle', () => {
            if (group.open) separate();
        });
    }
    // The Product Grid block's CLEAR ALL is a native reset: it restores
    // the slider and the value inputs, not their disabled state.
    if (form) {
        form.addEventListener('reset', () => window.setTimeout(() => {
            sync();
            paint();
        }, 0));
    }

    paint();
};

const init = () => {
    document.querySelectorAll('[data-shop-pressure-range]').forEach(initWrap);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
